import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { UtilitiesService } from './utilities.service';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { EventTrackingService } from './event-tracking.service';
import { WrenchApiService } from './wrench-api.service';
import { USER_EVENT_TYPE } from '../constants';
import {
    FindWorkOrderOptions, FindWorkOrderResponse,
    GetLatestChecklistOptions, GetLatestChecklistResponse, GetPhotosResponse,
    GetServiceReportsOptions, RescheduleJobOptions,
    UpdateApprovalCriteriaOptions
} from "../../shared/_models/job.service-options";
import { AddEstimateParams } from 'app/shared/_models/addEstimateParams';
import { GetQuoteParams } from 'app/shared/_models/getQuoteParams';
import { UserService } from './user.service';
import { map } from 'rxjs/internal/operators/map';
import { RoleService } from './role.service';
import { OrgContext, Role } from 'app/shared/_models/role';
import { CurrencyService } from './currency.service';
import { ConstantsService } from './constants.service';
import {GetPpmContractsRequest, GetPpmContractsResponse} from "../../shared/_models/ppm";

@Injectable()
export class JobService extends WrenchApiService {
    public rating = 0;
    selectedRoleOrgContext: OrgContext;

    constructor(
        protected http: HttpClient,
        protected authService: AuthService,
        protected router: Router,
        private roleService: RoleService,
        private currencyService: CurrencyService,
        protected eventTrackingService: EventTrackingService,
        private utilitiesService: UtilitiesService,
        private constantService: ConstantsService) {

        super(http, authService, router, eventTrackingService);

        // get selected role
        this.roleService.selectedUserRole$.subscribe((role: Role) => {
            if (role) {
                this.selectedRoleOrgContext = role.orgContext;
            } else {
                this.selectedRoleOrgContext = null;
            }
        });

    } // constructor()

    /**
     * gets a specific job by id
     */
    getJob(params: any, componentName: string) {
        return this.request(
            '/job/getjobs',
            params,
            componentName,
            USER_EVENT_TYPE.GET_JOB_REQUEST
        );
    } // getJob()

    getJobRequest(params: any, componentName: string = 'job-service') {
        return this.request(
            '/job/getjobrequest',
            params,
            componentName,
            USER_EVENT_TYPE.GET_JOB_REQUEST
        );
    } // getJobRequest()

    /**
     * gets a quote for a job when the user isn't logged in
     * @param params
     */
    unauthenticatedEstimate(params: any) {
        return this.http.post(this.authService.getBackendUrl('/transaction/getsimpleprice'), JSON.stringify(params), this.authService.getBasicAuthHeaders()).map((response: any) => {
            if (response.responseCode == 100) {
                this.currencyService.setCurrencyCode(response?.currency?.code);
            }
            return response;
        }).catch(error => { return this.handleError(error); });
    } // unauthenticatedEstimate()

    /**
     * gets a quote for a job when the user is logged in
     * @param params
     * @param component
     */
    getPrice(params: any, component: string = "") {
        return this.request(
            '/transaction/getprice',
            params,
            component,
            USER_EVENT_TYPE.GET_QUOTE
        );
    } // getPrice()

    /**
     * gets a quote for a job when the user is logged in
     * @param params
     * @param component
     */
    deprecatedGetPrice(params: any) {
        return this.http.post(this.authService.getBackendUrl('/transaction/getprice'), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => { return this.handleError(error); });
    } // deprecatedGetPrice()

    /**
     * gets a quote for a job when the user is logged in
     * @param params
     * @param component
     */
    getQuote(params: GetQuoteParams, component = 'job-service') {
        if (params.retrievePDF) {
            return this.http.post(this.authService.getBackendUrl('/transaction/getquote'), JSON.stringify(params), {
                headers: this.authService.getAuthHeaders().headers,
                responseType: 'blob',
                observe: 'response'
            })
                .map(UtilitiesService.downloadPdf)
                .catch(error => {
                    return this.handleError(error);
                });
        } else {
            return this.request(
                '/transaction/getquote',
                params,
                component,
                USER_EVENT_TYPE.GET_QUOTE
            );
        }
    } // getQuote()

    /**
     * checks the validity of a promo code
     * and shows details of the discount
     */
    getPromoDetails(params: any) {
        return this.http.post(this.authService.getBackendUrl('/transaction/getpromodetails'), JSON.stringify(params), this.authService.getBasicAuthHeaders())
            .catch(error => { return this.handleError(error); });

    } // getPromoDetails()

    addEstimate(params: AddEstimateParams) {
        return this.http.post(this.authService.getBackendUrl('/transaction/addestimate'), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });
    }

    /**
     * gets the booking hours based on service hours and a location id
     */
    getBookingHours(params: any, component: string = 'job-service') {
        return this.request(
            '/job/getbookinghours',
            params,
            component,
            USER_EVENT_TYPE.GET_BOOKING_HOURS
        );

    } // updateJob()

    /**
     * checks the availability of a desired time
     */
    getAvailability(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/getavailability'), JSON.stringify(params), this.authService.getBasicAuthHeaders())
            .catch(error => { return this.handleError(error); });

    } // getAvailability()

    getAvailabilityAuthed(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/getavailability'), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => { return this.handleError(error); });

    } // getAvailability()

    /**
     * update a job
     */
    updateJob(params: any, component: string) {
        return this.request(
            '/job/updatejob',
            params,
            component,
            USER_EVENT_TYPE.CHANGE
        );

    } // updateJob()

    /**
     * update job notes
     */
    updateJobNotes(params: any, component: string) {
        return this.request(
            '/job/updatejobnotes',
            params,
            component,
            USER_EVENT_TYPE.CHANGE
        );

    } // updateJob()

    /**
     * cancels a job
     */
    cancelJob(params: any, component: string) {
        return this.request(
            '/job/canceljob',
            params,
            component,
            USER_EVENT_TYPE.CANCEL_APPOINTMENT
        );
    } // cancelJob()

    /**
     * reschedule a job
     */
    rescheduleJob(params: RescheduleJobOptions, component: string) {
        return this.request(
            '/job/reschedulejob',
            params,
            component,
            USER_EVENT_TYPE.CHANGE
        );

    } // rescheduleJob()

    /**
     * cancels a job request
     */
    cancelJobRequest(params: any, component: string = 'job-service') {
        return this.request(
            '/job/canceljobrequest',
            params,
            component,
            USER_EVENT_TYPE.CANCEL_APPOINTMENT
        );
    } // cancelJob()

    /**
     * update a job request
     */
    updateJobRequest(params: any, component: string = 'job-service') {
        return this.request(
            '/job/updatejobrequest',
            params,
            component,
            USER_EVENT_TYPE.UPDATE_JOB_REQUEST
        );

    } // updateJob()

    updateJobRequestLocation(params: any, component: string) {
        return this.request(
            '/job/updatejobrequestlocation',
            params,
            component,
            USER_EVENT_TYPE.UPDATE_JOB_REQUEST
        );
    }

    /**
     * gets the last location of a provider
     */
    getJobTrace(params: any, component: string = 'job-service') {
        return this.request(
            '/job/getjobtrace',
            params,
            component,
            USER_EVENT_TYPE.GET_JOB_TRACE
        );
    } // getJobTrace()

    /**
     * gets the last location of a provider
     */
    getJobNotes(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/getjobnotes'), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => { return this.handleError(error); });

    } // getJobNotes()

    /**
     * gets the contact info of a provider for the job
     */
    getContactInfo(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/getcontactinfo'), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => { return this.handleError(error); });

    } // getContactInfo()

    /**
     * get a mechanic profile
     */
    getMechanicProfile(params: any) {
        return this.request(
            '/job/viewprofile',
            params,
            'job-service',
            USER_EVENT_TYPE.GET_MECHANIC_PROFILE,
            true
        );
    } // getMechanicProfile()

    /**
     * get the ratings for a mechanic based on the job
     */
    getRatings(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/getratings'), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => { return this.handleError(error); });

    } // getRatings()

    /**
     * rates a mechanic after a completed job
     */
    rateMechanic(params: any) {
        return this.request(
            '/job/rateprovider',
            params,
            'job-service',
            USER_EVENT_TYPE.RATE,
        );
    } // rateMechanic()

    /**
     * gets bundles
     * @param params
     * @param component
     */
    getServiceBundles(params: any, component: string = "") {
        if (this.authService.loggedIn()) {
            return this.request(
                '/catalog/getservicebundlesauthed',
                params,
                component,
                USER_EVENT_TYPE.GET_SERVICE_BUNDLES_AUTHED
            );
        } else {
            return this.request(
                '/catalog/getservicebundles',
                params,
                component,
                USER_EVENT_TYPE.GET_SERVICE_BUNDLES,
                true
            );
        }
    } // getServiceBundles()

    /**
     * gets maintenance services
     * @param params
     * @param component
     */
    getMaintenanceServices(params: any, component: string = "") {
        if (!this.constantService.getAppConstants().fetchVehicleServiceIntervals) {
            return of([]);
        }
        return this.request(
            '/catalog/getmaintenanceservices',
            params,
            component,
            USER_EVENT_TYPE.GET_MAINTENANCE_SERVICES,
            !this.authService.loggedIn()
        );
    } // getMaintenanceServices()

     /**
     * retrieves search tags for catalog entries
      * @deprecated
     */
    getCatalogSearchTags(component: string) {
        return this.request('/job/getcatalogsearchtags', {}, component, USER_EVENT_TYPE.LOAD_CATALOG_SEARCH_TAGS, true);
    } // getCatalogSearchTags()

    /**
     * @deprecated
     */
    private removeAttributesFromService(service: any) {
        const toRemove = ['id', 'transmission', 'drivetrain', 'vehicleType', 'fuelType', 'mileage', 'market', 'userId',
            'origin', 'status', 'createdby', 'updatedate', 'createdate', 'payerId'];
        for (let remove of toRemove) {
            delete service[remove];
        }
    }

    /**
     * @deprecated
     */
    private modifyCatalog(catalog: any[]) {
        for (let i = 0; i < catalog.length; i++) {
            if (catalog[i].service) {
                // set service.serviceCode
                catalog[i].service.serviceCode = catalog[i].service.code;
                // we need to remove the ids from the services
                this.removeAttributesFromService(catalog[i].service);
                // set service.productGroup
                if (UtilitiesService.isNotEmpty(catalog[i].productGroup)) {
                    catalog[i].service.productGroup = catalog[i].productGroup;
                }
            } else {
                for (let j = 0; j < catalog[i].options.length; j++) {
                    // set option.service.serviceCode
                    catalog[i].options[j].service.serviceCode = catalog[i].options[j].service.code;
                    // remove the ids from the services
                    this.removeAttributesFromService(catalog[i].options[j].service);
                    // set service.productGroup
                    if (UtilitiesService.isNotEmpty(catalog[i].productGroup)) {
                        catalog[i].options[j].service.productGroup = catalog[i].productGroup;
                    }
                }
            }
        }
    }

    /**
     * get the current catalog of our jobs
     * @deprecated
     */
    getCatalog(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/getcatalog'), JSON.stringify(params), this.authService.getBasicAuthHeaders())
            .map((response: any) => {
                if (response.responseCode == 100) {
                    this.modifyCatalog(response.catalog);
                }
                if (!UtilitiesService.isEmpty(response.market)) {
                    UserService._marketData.next(response.market);
                }
                return response;
            })
            .catch(error => { return this.handleError(error); });

    } // getCatalog()

    /**
     * get the current catalog of our jobs
     * @deprecated
     */
    getCatalogAuthed(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/getcatalogauthed'), JSON.stringify(params), this.authService.getAuthHeaders())
            .map((resp: any) => {
                if (resp.responseCode == 100) {
                    this.modifyCatalog(resp.catalog);
                }
                if (!UtilitiesService.isEmpty(resp.market)) {
                    UserService._marketData.next(resp.market);
                }
                return resp;
            })
            .catch(error => { return this.handleError(error); });

    } // getCatalogAuthed()

    /**
     * get addons for new users
     * @deprecated
     */
    getAddOnCatalog(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/getproductaddons'), JSON.stringify(params), this.authService.getBasicAuthHeaders())
            .map((resp: any) => {
                if (resp.responseCode == 100) {
                    this.currencyService.setCurrencyCode(resp?.currency?.code);
                    this.modifyCatalog(resp.addons);
                }
                return resp;
            })
            .catch(error => { return this.handleError(error); });

    } // getAddOnCatalog()

    /**
     * get addons with authentication
     * @deprecated
     */
    getAddOnCatalogAuthed(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/getproductaddonsauthed'), JSON.stringify(params), this.authService.getAuthHeaders())
            .map((resp: any) => {
                if (resp.responseCode == 100) {
                    this.currencyService.setCurrencyCode(resp?.currency?.code);
                    this.modifyCatalog(resp.addons);
                }
                return resp;
            })
            .catch(error => { return this.handleError(error); });

    } // getAddOnCatalogAuthed()

    getQuotesToApprove(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/getquotestoapprove'), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => { return this.handleError(error); });

    } // getQuotesToApprove()

    updateJobRequestStatus(params: any, component: string = 'job-service') {
        return this.request(
            '/job/updatejobrequeststatus',
            params,
            component,
            USER_EVENT_TYPE.UPDATE_JOB_REQUEST
        );
    } // updateJobRequestStatus()

    /*
    * add contact to a job
     */
    addContact(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/addcontact'), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => { return this.handleError(error); });

    } // getQuotesToApprove()

    getPhotoCredentials(): Observable<any> {
        let awsCredentials: any = {};
        try {
            awsCredentials = JSON.parse(this.utilitiesService.getLocal('aws'));
        } catch (e) {
            UtilitiesService.consoleLog(e);
        }

        if (!this.utilitiesService.isEmpty(awsCredentials) && !this.utilitiesService.isEmpty(awsCredentials.expiration) && Date.parse(awsCredentials.expiration) > Date.now()) {
            return of({ credentials: awsCredentials });
        } else {
            return this.http.post(this.authService.getBackendUrl('/job/getphotocredentials'), JSON.stringify({}), this.authService.getAuthHeaders())
                .map((resp: any) => {
                    if (!this.utilitiesService.isEmpty(resp) && !this.utilitiesService.isEmpty(resp.credentials)) {
                        this.utilitiesService.setLocal('aws', JSON.stringify(resp.credentials));
                        return { credentials: resp.credentials };
                    } else {
                        return {};
                    }
                })
                .catch(error => { return this.handleError(error); });
        }
    } // getPhotoCredentials()

    /**
     * gets AWS credentials
     */
    getAWSCredentials(component: string = 'job-service') {
        return this.request(
            '/job/getawscredentials',
            {},
            component,
            USER_EVENT_TYPE.CANCEL_APPOINTMENT
        );
    } // getAWSCredentials()

    savePhotos(params: any) {
        return this.request(
            '/job/savephotos',
            params,
            'TODO',
            USER_EVENT_TYPE.SAVE_PHOTOS
        );
    } // savePhotos()

    deprecatedSavePhotos(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/savephotos'), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => { return this.handleError(error); });
    } // savePhotos()

    getPhotos(params: any, componentName: string): Observable<GetPhotosResponse> {
        return this.request(
            '/job/getphotos',
            params,
            componentName,
            USER_EVENT_TYPE.GET_PHOTOS
        );
    } // getPhotos()

    removePhoto(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/removephoto'), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => { return this.handleError(error); });
    } // removePhotos()

    updatePhotos(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/updatephotos'), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => { return this.handleError(error); });
    } // updatePhotos()

    getPriceHistory(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/getpricehistory'), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => { return this.handleError(error); });
    } // getPriceHistory()

    approvePrice(params: any) {
        return this.http.post(this.authService.getBackendUrl('/job/approveprice'), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => { return this.handleError(error); });
    } // approvePrice()

    /**
     *
     * @param params (jobRequestId)
     */
    deprecatedAssignPartnerJob(params: { id: number }) {
        return this.http.post(this.authService.getBackendUrl('/job/assignpartnerjob'), JSON.stringify(params), this.authService.getAuthHeaders())
            .catch(error => { return this.handleError(error); });
    } // assignPartnerJob()

    assignPartnerJob(params: { id: number }, componentName: string) {
        return this.request(
            '/job/assignpartnerjob',
            params,
            componentName,
            null
        );
    }

    public updateApprovalCriteria(params: UpdateApprovalCriteriaOptions, componentName: string, userEvent: string): Observable<any> {
        return this.request(
            '/job/updateapprovalcriteria',
            params,
            componentName,
            userEvent
        );
    }

    private getRecords(recordList: any[]) {
        const records: any[] = [];
        if (recordList) {
            for (let record of recordList) {
                const containsItem = records.some((item) => {
                    if (record.productId !== 0) {
                        return item.productId === record.productId;
                    }
                    return item.name === record.name;
                });
                if (!containsItem) {
                    records.push(record);
                }
            }
        }
        return records;
    }

    getApprovalCriteria(componentName: string, userEvent: string) {
        const params: any = {};

        if (this.selectedRoleOrgContext) {
            params.organizationId = this.selectedRoleOrgContext.organizationId;
            // Approval Criteria is currently only associated with an organization.
        }
        return this.request(
            '/job/getapprovalcriteria',
            params,
            componentName,
            userEvent
        ).pipe(
            map((response) => {
                response.catalogEntryRecordList = this.getRecords(response.catalogEntryRecordList);
                response.catalogOptionRecordList = this.getRecords(response.catalogOptionRecordList);
                return response;
            })
        );
    }

    getLatestChecklist(params: GetLatestChecklistOptions, componentName: string, userEvent: string): Observable<GetLatestChecklistResponse> {
        return this.request(
            '/job/getlatestchecklist',
            params,
            componentName,
            userEvent
        );
    }

    getServiceReports(params: GetServiceReportsOptions) {
        return this.http.post(this.authService.getBackendUrl('/job/getservicereports'), JSON.stringify(params), {
            headers: this.authService.getAuthHeaders().headers,
            responseType: 'blob',
            observe: 'response'
        })
            .map(UtilitiesService.downloadPdf)
            .catch(error => {
                return this.handleError(error);
            });
    } // getServicesReports()

    getDiagDecision(params: any, component: string = 'job-service') {
        return this.request(
            '/job/getdiagdecision',
            params,
            component,
            USER_EVENT_TYPE.GET_DIAG_DECISION,
            true
        );
    } // getDiagDecision()

    getDiagGraph(component: string = 'job-service', params: any) {
        return this.request(
            '/job/getdiaggraph',
            params,
            component,
            USER_EVENT_TYPE.GET_DIAG_GRAPH,
            true
        );
    } // getDiagGraph()

    getMbsAvailability(params: any, component: string) {
        return this.request(
            '/job/getmbsavailability',
            params,
            component,
            USER_EVENT_TYPE.CHECK_MBS_AVAILABILITY,
            true
        );
    } // getMbsAvailability()

    getBulkJrGroups(params: any, component: string) {
        return this.request(
            '/job/getbulkjrgroups',
            params,
            component,
            USER_EVENT_TYPE.GET_BULK_JR_GROUPS
        );
    } // getBulkJrGroups()

    getBulkAvailability(params: any, component: string) {
        return this.request(
            '/job/getbulkavailability',
            params,
            component,
            USER_EVENT_TYPE.GET_BULK_JR_GROUPS
        );
    } // getBulkAvailability()

    scheduleJobRequests(params: any, component: string) {
        return this.request(
            '/job/schedulejobrequests',
            params,
            component,
            USER_EVENT_TYPE.SCHEDULE_JOB_REQUESTS
        );
    } // scheduleJobRequests()

    unscheduleJobRequests(params: any, component: string) {
        return this.request(
            '/job/unschedulejobrequests',
            params,
            component,
            USER_EVENT_TYPE.UNSCHEDULE_JOB_REQUESTS
        );
    } // unscheduleJobRequests()

    findWorkOrders(params: {searchCriteria: FindWorkOrderOptions[]}, component: string, ): Observable<FindWorkOrderResponse> {
        if (this.selectedRoleOrgContext) {
            params.searchCriteria[0].organizationIds = this.selectedRoleOrgContext.organizationId > 0 ? [this.selectedRoleOrgContext.organizationId] : null;
            params.searchCriteria[0].regionIds = this.selectedRoleOrgContext.regionId > 0 ? [this.selectedRoleOrgContext.regionId] : null;
            params.searchCriteria[0].hubIds = this.selectedRoleOrgContext.hubId > 0 ? [this.selectedRoleOrgContext.hubId] : null;
        }
        return this.request(
            '/job/findworkorders',
            params,
            component,
            USER_EVENT_TYPE.FIND_WORK_ORDERS
        );
    }

    getPpmContracts(params: GetPpmContractsRequest, component: string): Observable<GetPpmContractsResponse> {
        return this.request(
            '/job/getppmcontracts',
            params,
            component,
            USER_EVENT_TYPE.GET_PPMS
        );
    }

}
